import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, Legend } from 'recharts';
import type { RevenueMetrics } from '@/lib/admin/analytics';

interface RevenuePotentialChartProps {
  revenue: RevenueMetrics;
}

const chartConfig = {
  sold: {
    label: 'Sold Value',
    color: 'hsl(213, 29%, 26%)', // brand-navy
  },
  reserved: {
    label: 'Reserved Value',
    color: 'hsl(197, 30%, 55%)', // champagne-light
  },
  available: {
    label: 'Available Value',
    color: 'hsl(197, 24%, 44%)', // brand-teal
  },
};

export function RevenuePotentialChart({ revenue }: RevenuePotentialChartProps) {
  const chartData = [
    {
      category: 'Revenue',
      Sold: revenue.soldValue,
      Reserved: revenue.reservedValue,
      Available: revenue.availableValue,
    },
  ];

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    return `$${(value / 1000).toFixed(0)}K`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenue Potential</CardTitle>
        <CardDescription>Portfolio value breakdown by status</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer
          config={chartConfig}
          className="h-[300px] w-full"
        >
          <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" />
            <YAxis
              tickFormatter={(value) => formatCurrency(value)}
            />
            <ChartTooltip
              content={<ChartTooltipContent />}
              formatter={(value: number) => [
                new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(value),
                'Value',
              ]}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="Sold"
              stackId="1"
              stroke="var(--color-sold)"
              fill="var(--color-sold)"
              fillOpacity={0.8}
            />
            <Area
              type="monotone"
              dataKey="Reserved"
              stackId="1"
              stroke="var(--color-reserved)"
              fill="var(--color-reserved)"
              fillOpacity={0.8}
            />
            <Area
              type="monotone"
              dataKey="Available"
              stackId="1"
              stroke="var(--color-available)"
              fill="var(--color-available)"
              fillOpacity={0.8}
            />
          </AreaChart>
        </ChartContainer>
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-sm text-muted-foreground">Sold</div>
            <div className="text-lg font-semibold text-red-500">
              {formatCurrency(revenue.soldValue)}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Reserved</div>
            <div className="text-lg font-semibold text-amber-500">
              {formatCurrency(revenue.reservedValue)}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Available</div>
            <div className="text-lg font-semibold text-green-600">
              {formatCurrency(revenue.availableValue)}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total Portfolio Value</span>
            <span className="text-xl font-bold">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(revenue.totalPortfolioValue)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

